/**
 * Draws one field of the plan (forms/plan.ts) by its control: plain inputs, people as picker
 * cards, categories as chips, links and the availability window as small groups. Values are read
 * and written by path; a cleared value is removed from the document.
 */

import { useState } from "react";
import Button from "@web-ui/components/Button";
import Checkbox from "@web-ui/components/Checkbox";
import Field from "@web-ui/components/Field";
import Modal from "@web-ui/components/Modal";
import Select from "@web-ui/components/Select";
import Switch from "@web-ui/components/Switch";
import type { Entity } from "@/api/admin";
import { useStudio } from "@/shell/studio";
import { lower, noun } from "@/vocabulary/vocabulary";
import { useForm } from "../form-context";
import { fieldDomId, getPath } from "../paths";
import type { FieldPlan, Option } from "../plan";
import { dateOf, fromDate, fromLocalInput, toLocalInput, withYear, yearOf } from "../time";
import { Group, LocalizedField } from "./inputs";
import { ItemPicker, PickerCard, RowActions } from "./picker";
import { moveItem } from "@/screens/rows";

type Doc = Record<string, unknown>;
interface Credit { personId: string; role: string; character?: Record<string, string> }

export function OptionSelect({ id, label, value, options, onChange, allowNone, help, error, disabled }: { id?: string; label: string; value: string | undefined; options: Option[]; onChange: (v: string | undefined) => void; allowNone?: boolean; help?: string; error?: string; disabled?: boolean }) {
  const list = [...(allowNone ? [{ value: "", label: "Not set" }] : []), ...options];
  const known = list.some((o) => o.value === (value ?? ""));
  const all = known || !value ? list : [...list, { value, label: value }];
  const items = all.map((o, i) => ({ id: i, name: o.label }));
  const current = all.findIndex((o) => o.value === (value ?? ""));
  return (
    <div id={id} tabIndex={-1} className="flex flex-col gap-1 outline-none" data-invalid={error ? true : undefined}>
      {disabled ? <Field label={label} value={all[current]?.label ?? ""} disabled readOnly /> : <Select label={label} placeholder="Choose" value={current >= 0 ? items[current]! : null} options={items} onChange={(o) => onChange(all[o.id]!.value || undefined)} />}
      {help && !error ? <p className="text-caption text-t-secondary">{help}</p> : null}
      {error ? <p className="textfield__error" role="alert">{error}</p> : null}
    </div>
  );
}

export function FieldControl({ field }: { field: FieldPlan }) {
  const form = useForm();
  const { doc, set, touch, canEdit } = form;
  const c = field.control;
  const value = getPath(doc, field.path.split(":")[0]!);
  const error = form.issue(field.path)?.message ?? form.issuesUnder(field.path.split(":")[0]!).find((i) => i.path === field.path.split(":")[0])?.message;
  const id = fieldDomId(field.path);
  const blur = () => touch(field.path);

  switch (c.kind) {
    case "text":
      return <Field id={id} label={field.label} textarea={c.multiline} placeholder={c.placeholder} value={typeof value === "string" ? value : value === undefined ? "" : String(value)} optional={!field.required} description={field.help} error={error} disabled={!canEdit}
        onChange={(e) => set(field.path, e.target.value)} onBlur={(e) => { set(field.path, e.target.value.trim() || undefined); blur(); }} />;

    case "localized":
      return <LocalizedField path={field.path} label={field.label} multiline={c.multiline} required={field.required} help={field.help} />;

    case "number":
      return <Field id={id} label={c.unit ? `${field.label} (${c.unit})` : field.label} type="number" inputMode={c.integer ? "numeric" : "decimal"} min={c.min} step={c.step ?? (c.integer ? 1 : "any")} optional={!field.required} description={field.help} error={error} disabled={!canEdit}
        value={typeof value === "number" ? String(value) : ""} onChange={(e) => set(field.path, e.target.value === "" ? undefined : Number(e.target.value))} onBlur={blur} />;

    case "select":
      return <OptionSelect id={id} label={field.label} value={value as string | undefined} options={c.options} allowNone={c.allowNone} help={field.help} error={error} disabled={!canEdit} onChange={(v) => { set(field.path, v); blur(); }} />;

    case "year":
      return <Field id={id} label={field.label} inputMode="numeric" maxLength={4} placeholder="e.g. 2012" optional description={field.help} error={error} disabled={!canEdit}
        value={yearOf(value)} onChange={(e) => { const y = e.target.value.replace(/\D/g, ""); set(field.path, y.length === 4 ? withYear(value, y) : y ? value : undefined); }} onBlur={blur} />;

    case "date":
      return <Field id={id} label={field.label} type="date" optional description={field.help} error={error} disabled={!canEdit} value={dateOf(value)} onChange={(e) => set(field.path, fromDate(e.target.value))} onBlur={blur} />;

    case "datetime":
      return <Field id={id} label={field.label} type="datetime-local" optional={!field.required} description={field.help} error={error} disabled={!canEdit} value={toLocalInput(value as string | undefined)} onChange={(e) => set(field.path, fromLocalInput(e.target.value))} onBlur={blur} />;

    case "boolean":
    case "kids":
      return (
        <div id={id} tabIndex={-1} className="flex items-center justify-between gap-4 outline-none">
          <div><p className="text-body-2">{field.label}</p>{field.help ? <p className="text-caption text-t-secondary">{field.help}</p> : null}</div>
          <Switch checked={value === true} onChange={(on) => set(field.path, on ? true : undefined)} ariaLabel={field.label} disabled={!canEdit} />
        </div>
      );

    case "people":
      return <PeopleField field={field} role={c.role} />;

    case "otherCredits":
      return <OtherCredits field={field} skip={c.skipRoles} />;

    case "team": {
      const participants = (doc["participants"] as Array<Doc> | undefined) ?? [];
      const mine = participants.find((p) => p["side"] === c.side);
      const nameText = (mine?.["name"] as Record<string, string> | undefined)?.[form.locale] ?? "";
      return <Field id={id} label={field.label} optional value={nameText} disabled={!canEdit} onBlur={blur}
        onChange={(e) => {
          const others = participants.filter((p) => p["side"] !== c.side);
          const next = e.target.value ? [...others, { ...(mine ?? { id: c.side }), side: c.side, name: { ...((mine?.["name"] as Record<string, string>) ?? {}), [form.locale]: e.target.value } }] : others;
          set("participants", next.sort((a, b) => (a["side"] === "home" ? -1 : b["side"] === "home" ? 1 : 0)));
        }} />;
    }

    case "ref":
      return <RefField field={field} types={c.types} />;

    case "ageBands": {
      const bands = (value as string[] | undefined) ?? [];
      return (
        <Group path={field.path} label={field.label} help={field.help} optional>
          <div className="flex flex-wrap gap-4">
            {["2-4", "5-7", "8-10"].map((b) => (
              <Checkbox key={b} label={`${b.replace("-", " to ")} years`} checked={bands.includes(b)} disabled={!canEdit} onChange={(on) => set(field.path, on ? [...bands, b].sort() : bands.filter((x) => x !== b))} />
            ))}
          </div>
        </Group>
      );
    }

    case "terms":
      return <TermsField field={field} />;

    case "tags":
      return <TagsField field={field} />;

    case "links":
      return <LinksField field={field} />;

    case "attribution": {
      const a = (value as Doc | undefined) ?? {};
      return (
        <Group path={field.path} label={field.label} help={field.help} optional>
          <div className="grid gap-3 md:grid-cols-3">
            <Field id={fieldDomId("attribution.text")} label="Credit line" size="sm" placeholder="e.g. © Blender Foundation" value={String(a["text"] ?? "")} error={form.issue("attribution.text")?.message} disabled={!canEdit} onChange={(e) => set("attribution.text", e.target.value)} onBlur={() => touch("attribution.text")} />
            <Field label="Licence" size="sm" optional placeholder="e.g. CC BY 3.0" value={String(a["licence"] ?? "")} disabled={!canEdit} onChange={(e) => set("attribution.licence", e.target.value)} />
            <Field id={fieldDomId("attribution.url")} label="Link" size="sm" optional placeholder="https://" value={String(a["url"] ?? "")} error={form.issue("attribution.url")?.message} disabled={!canEdit} onChange={(e) => set("attribution.url", e.target.value.trim())} onBlur={() => touch("attribution.url")} />
          </div>
        </Group>
      );
    }

    case "availability": {
      const a = (value as { from?: string; until?: string; countries?: string[] } | undefined) ?? {};
      return (
        <Group path={field.path} label={field.label} help={field.help} optional>
          <div className="grid gap-3 md:grid-cols-3">
            <Field id={fieldDomId("availability.from")} label="From" size="sm" type="datetime-local" optional value={toLocalInput(a.from)} disabled={!canEdit} onChange={(e) => set("availability.from", fromLocalInput(e.target.value))} />
            <Field id={fieldDomId("availability.until")} label="Until" size="sm" type="datetime-local" optional value={toLocalInput(a.until)} error={form.issue("availability.until")?.message} disabled={!canEdit} onChange={(e) => set("availability.until", fromLocalInput(e.target.value))} onBlur={() => touch("availability.until")} />
            <CountriesField countries={a.countries ?? []} />
          </div>
        </Group>
      );
    }

    case "rating": {
      const r = (value as { system?: string; value?: string } | undefined) ?? {};
      const systems: Option[] = [["kijkwijzer", "Kijkwijzer (NL)"], ["bbfc", "BBFC (UK)"], ["mpaa", "MPA (US)"], ["fsk", "FSK (DE)"], ["pegi", "PEGI"], ["tv", "TV parental guidelines"], ["age", "Minimum age"]].map(([v, l]) => ({ value: v!, label: l! }));
      return (
        <Group path={field.path} label={field.label} optional error={error}>
          <div className="grid gap-3 md:grid-cols-2">
            <OptionSelect label="System" value={r.system} options={systems} allowNone disabled={!canEdit} onChange={(v) => set("rating.system", v)} />
            <Field label="Rating" size="sm" placeholder="e.g. 12, PG, 6+" value={r.value ?? ""} disabled={!canEdit} onChange={(e) => set("rating.value", e.target.value)} onBlur={() => touch("rating")} />
          </div>
        </Group>
      );
    }

    case "timeZone":
      return <Field id={id} label={field.label} optional placeholder="Europe/Amsterdam" description="The city its time is announced in, e.g. Europe/Amsterdam. Times always show in each viewer's own zone." value={typeof value === "string" ? value : ""} disabled={!canEdit} onChange={(e) => set(field.path, e.target.value.trim())} onBlur={blur} />;

    case "readonlyId":
      return (
        <div id={id} tabIndex={-1} className="flex flex-col gap-1 outline-none">
          <span className="textfield__label">{field.label}</span>
          <code className="w-fit rounded-lg bg-b-surface1 px-2 py-1 text-body-2">{form.id}</code>
          {field.help ? <p className="text-caption text-t-secondary">{field.help}</p> : null}
        </div>
      );

    case "externalIds": {
      const ids = Object.entries((value as Record<string, string> | undefined) ?? {});
      if (!ids.length) return null;
      return (
        <div id={id} tabIndex={-1} className="flex flex-col gap-1 outline-none">
          <span className="textfield__label">{field.label}</span>
          <ul className="text-body-2 text-t-secondary">{ids.map(([k, v]) => <li key={k}>{k}: <code>{v}</code></li>)}</ul>
        </div>
      );
    }
  }
}

function CountriesField({ countries }: { countries: string[] }) {
  const { set, issuesUnder, canEdit } = useForm();
  const [textValue, setText] = useState(countries.join(", "));
  const error = issuesUnder("availability.countries")[0]?.message;
  return <Field id={fieldDomId("availability.countries")} label="Only in" size="sm" optional placeholder="e.g. NL, BE" description={error ? undefined : "Country codes. Empty means everywhere."} error={error} value={textValue} disabled={!canEdit}
    onChange={(e) => setText(e.target.value)} onBlur={() => set("availability.countries", textValue.split(/[\s,;]+/).map((s) => s.trim().toUpperCase()).filter(Boolean))} />;
}

function TagsField({ field }: { field: FieldPlan }) {
  const { doc, set, canEdit } = useForm();
  const tags = (getPath(doc, field.path) as string[] | undefined) ?? [];
  const [textValue, setText] = useState(tags.join(", "));
  return <Field id={fieldDomId(field.path)} label={field.label} optional description={field.help} value={textValue} disabled={!canEdit}
    onChange={(e) => setText(e.target.value)} onBlur={() => set(field.path, [...new Set(textValue.split(",").map((s) => s.trim()).filter(Boolean))])} />;
}

/** The people credited in one role, as cards; add from the brand's people. */
function PeopleField({ field, role }: { field: FieldPlan; role: string }) {
  const form = useForm();
  const { pack } = useStudio();
  const [open, setOpen] = useState(false);
  const credits = (form.doc["credits"] as Credit[] | undefined) ?? [];
  const mine = credits.filter((c) => c.role === role);
  const byId = (id: string) => form.catalogue?.find((e) => e.type === "person" && e.id === id);
  const write = (next: Credit[]) => form.set("credits", [...credits.filter((c) => c.role !== role), ...next]);
  return (
    <Group path={field.path} label={field.label} help={field.help} optional>
      {mine.length ? (
        <ul className="flex flex-col gap-2">
          {mine.map((c, i) => (
            <li key={c.personId}>
              <PickerCard entity={byId(c.personId)} missingId={c.personId} actions={form.canEdit ? <RowActions name={byId(c.personId)?.label ?? c.personId} index={i} count={mine.length} onMove={(to) => write(moveItem(mine, i, to))} onRemove={() => write(mine.filter((x) => x !== c))} /> : undefined} />
            </li>
          ))}
        </ul>
      ) : null}
      {form.canEdit ? <div><Button type="button" size="sm" variant="secondary" icon="plus" onClick={() => setOpen(true)}>{mine.length ? `Add another ${field.label.toLowerCase()}` : `Choose the ${field.label.toLowerCase()}`}</Button></div> : null}
      <ItemPicker open={open} onClose={() => setOpen(false)} types={["person"]} catalogue={form.catalogue} exclude={mine.map((c) => c.personId)} title={`Choose the ${field.label.toLowerCase()}`}
        onPick={(ids) => write([...mine, ...ids.map((personId) => ({ personId, role }))])} />
      {!form.catalogue?.some((e) => e.type === "person") && form.catalogue ? <p className="text-caption text-t-secondary">{`Add ${lower(noun(pack, "person", "many"))} in your Library first.`}</p> : null}
    </Group>
  );
}

/** Credits in roles the Basics do not ask for: a person and what they did. */
function OtherCredits({ field, skip }: { field: FieldPlan; skip: string[] }) {
  const form = useForm();
  const [open, setOpen] = useState(false);
  const credits = (form.doc["credits"] as Credit[] | undefined) ?? [];
  const rows = credits.map((c, index) => ({ c, index })).filter((x) => !skip.includes(x.c.role));
  const byId = (id: string) => form.catalogue?.find((e) => e.type === "person" && e.id === id);
  return (
    <Group path={field.path} label={field.label} help={field.help} optional>
      {rows.map(({ c, index }) => (
        <div key={`${c.personId}:${index}`} className="grid items-end gap-2 md:grid-cols-[minmax(0,1fr)_14rem_auto]">
          <PickerCard entity={byId(c.personId)} missingId={c.personId} />
          <Field id={fieldDomId(`credits.${index}.role`)} label="Role" size="sm" placeholder="e.g. writer" value={c.role} error={form.issue(`credits.${index}.role`)?.message} disabled={!form.canEdit}
            onChange={(e) => form.set(`credits.${index}.role`, e.target.value)} onBlur={() => form.touch(`credits.${index}.role`)} />
          {form.canEdit ? <Button type="button" size="xs" variant="ghost" icon="close" isIconOnly aria-label={`Remove ${byId(c.personId)?.label ?? c.personId}`} onClick={() => form.set("credits", credits.filter((_, j) => j !== index))} /> : null}
        </div>
      ))}
      {form.canEdit ? <div><Button type="button" size="sm" variant="secondary" icon="plus" onClick={() => setOpen(true)}>Add a credit</Button></div> : null}
      <ItemPicker open={open} onClose={() => setOpen(false)} types={["person"]} catalogue={form.catalogue} title="Who to credit" onPick={(ids) => form.set("credits", [...credits, ...ids.map((personId) => ({ personId, role: "" }))])} />
    </Group>
  );
}

function RefField({ field, types }: { field: FieldPlan; types: Array<Entity["type"]> }) {
  const form = useForm();
  const [open, setOpen] = useState(false);
  const value = getPath(form.doc, field.path) as string | undefined;
  const entity = value ? form.catalogue?.find((e) => types.includes(e.type) && e.id === value) : undefined;
  return (
    <Group path={field.path} label={field.label} help={field.help} optional>
      {value ? <PickerCard entity={entity} missingId={value} actions={form.canEdit ? <Button type="button" size="xs" variant="ghost" icon="close" isIconOnly aria-label={`Remove ${entity?.label ?? value}`} onClick={() => form.set(field.path, undefined)} /> : undefined} /> : null}
      {form.canEdit ? <div><Button type="button" size="sm" variant="secondary" onClick={() => setOpen(true)}>{value ? "Change" : "Choose"}</Button></div> : null}
      <ItemPicker open={open} onClose={() => setOpen(false)} types={types} catalogue={form.catalogue} multiple={false} title={`Choose the ${field.label.toLowerCase()}`} onPick={([id]) => form.set(field.path, id)} />
    </Group>
  );
}

/** Categories: chips of what is chosen, and a list of every category to tick. */
function TermsField({ field }: { field: FieldPlan }) {
  const form = useForm();
  const [open, setOpen] = useState(false);
  const chosen = (getPath(form.doc, field.path) as string[] | undefined) ?? [];
  const lists = (form.catalogue ?? []).filter((e) => e.type === "taxonomy");
  const termName = (id: string) => {
    for (const l of lists) { const t = ((l.data["terms"] as Array<{ id: string; title: Record<string, string> }>) ?? []).find((x) => x.id === id); if (t) return t.title[form.locale] ?? Object.values(t.title)[0] ?? id; }
    return id;
  };
  const toggle = (id: string, on: boolean) => form.set(field.path, on ? [...chosen, id] : chosen.filter((x) => x !== id));
  return (
    <Group path={field.path} label={field.label} help={field.help} optional>
      {chosen.length ? (
        <ul className="flex flex-wrap gap-2">
          {chosen.map((id) => (
            <li key={id} className="flex items-center gap-1 rounded-full bg-b-surface1 py-1 ps-3 pe-1 text-body-2">
              {termName(id)}
              {form.canEdit ? <Button type="button" size="xs" variant="ghost" icon="close" isIconOnly aria-label={`Remove ${termName(id)}`} onClick={() => toggle(id, false)} /> : null}
            </li>
          ))}
        </ul>
      ) : null}
      {form.canEdit ? <div><Button type="button" size="sm" variant="secondary" onClick={() => setOpen(true)}>{chosen.length ? "Change" : "Choose"}</Button></div> : null}
      <Modal open={open} onClose={() => setOpen(false)} size="md">
        <div className="flex flex-col gap-4">
          <h2 className="text-h5">{field.label}</h2>
          {lists.length === 0 ? <p className="text-body-2 text-t-secondary">There are no lists yet. Add one under {field.label} in your Library.</p> : null}
          {lists.map((l) => (
            <fieldset key={l.id} className="flex flex-col gap-2">
              <legend className="mb-1 text-sub-title-2">{l.label}</legend>
              {((l.data["terms"] as Array<{ id: string; title: Record<string, string> }>) ?? []).map((t) => (
                <Checkbox key={t.id} label={t.title[form.locale] ?? Object.values(t.title)[0] ?? t.id} checked={chosen.includes(t.id)} onChange={(on) => toggle(t.id, on)} />
              ))}
            </fieldset>
          ))}
          <div><Button type="button" variant="primary" onClick={() => setOpen(false)}>Done</Button></div>
        </div>
      </Modal>
    </Group>
  );
}

const LINK_KINDS: Option[] = [["give", "Give"], ["tickets", "Tickets"], ["website", "Website"], ["shop", "Shop"], ["contact", "Contact"], ["prayer", "Prayer"]].map(([v, l]) => ({ value: v!, label: l! }));

function LinksField({ field }: { field: FieldPlan }) {
  const form = useForm();
  const links = (form.doc["links"] as Array<{ kind: string; title?: Record<string, string>; url?: string }> | undefined) ?? [];
  return (
    <Group path={field.path} label={field.label} help={field.help} optional>
      {links.map((l, i) => (
        <div key={i} className="grid items-start gap-2 rounded-2xl bg-b-surface1 p-3 md:grid-cols-[10rem_minmax(0,1fr)_minmax(0,1fr)_auto]">
          <OptionSelect label="Kind" value={l.kind} options={LINK_KINDS} disabled={!form.canEdit} onChange={(v) => form.set(`links.${i}.kind`, v ?? "website")} />
          <LocalizedField path={`links.${i}.title`} label="Label" size="sm" required />
          <Field id={fieldDomId(`links.${i}.url`)} label="Address" size="sm" placeholder="https://" value={l.url ?? ""} error={form.issue(`links.${i}.url`)?.message} disabled={!form.canEdit}
            onChange={(e) => form.set(`links.${i}.url`, e.target.value.trim())} onBlur={() => form.touch(`links.${i}.url`)} />
          {form.canEdit ? <Button className="md:mt-7" type="button" size="xs" variant="ghost" icon="close" isIconOnly aria-label={`Remove link ${i + 1}`} onClick={() => form.set("links", links.filter((_, j) => j !== i))} /> : null}
        </div>
      ))}
      {form.canEdit ? <div><Button type="button" size="sm" variant="secondary" icon="plus" onClick={() => form.set("links", [...links, { kind: "website" }])}>Add a link</Button></div> : null}
    </Group>
  );
}
